import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Grid,
  GridColumn,
  GridNoRecords,
  getSelectedState,
} from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { Splitter } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { formatDateForAPI } from "components/common/formatter.js";
import {
  getDocument,
  downloadDocumentAsPDF,
  downloadDocument,
  getDocumentEditor,
  getMatterDocDuplicate,
  getMattersdetails,
  getSingleMatterActivity,
  updateMatterActivity,
  downloadDocumentsAsZip,
} from "api/index";
import { getBillingRequestDetails } from "services/billingRequestsService";
import { useTeamAccess } from "services/currentUserService";
import { feeEarnerRefIfAllowed } from "Helper";

import DocumentViewer from "components/common/DocumentViewer";
import { fileIcon } from "components/common/fileIcon";
import { HandleGridBody } from "components/common/HandleGridBody";
import { getActiveActivityTypes } from "services/activityTypeService";
import { useCurrentUser } from "services/currentUserService";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useDoubleClick } from "components/common/hooks/SingleDoubleClick";
import { Button } from "@progress/kendo-react-buttons";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import SearchTextBox from "components/SearchComponent";
import { FaFilter } from "react-icons/fa";
import filtersAppliedIcon from "../../assets/CaseFileIcons/FilterApplied.svg";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import styles from "./WorkDone.module.scss";
import MenuCell from "components/Activities/MenuCell";
import {
  ACTIVITY_DATA_ITEM_KEY,
  ACTIVITY_SELECTED_FIELD,
  ActivityAmountCell,
  ActivityTagsCell,
  ActivityUnchargedCell,
  ActivityUnitsCell,
  buildMappedGridData,
  downloadBlobAsFile,
  getSelectedActivities,
  getActivitySubtypeStatus,
  getActivityTagChipStyle,
  getActivityTagId,
  getDeleteConfirmationMessage,
  getDeleteSuccessMessage,
  mergeDuplicateActivityData,
  resolveDuplicateActivityId,
  getRowStyle,
  getSelectedDocumentIds,
  getSelectedDocumentsForShare,
  getStatusClass,
  parseDownloadFilename,
  truncateFileName,
} from "components/Activities/activityUtils";
import ShareDocumentModal from "../CaseFile/ShareDocumentModal";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import ESigning from "../CaseFile/ESigning";
import CaseActivityForm from "components/Activities/CaseActivityForm";
import ChitRequest from "pages/ChitRequest/ChitRequest";
import BillPostingModal from "pages/Bills/BillRequestModal.jsx";
import { isNonEmptyBillResourceId } from "pages/Bills/billReportResourceId";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { clearMattersBalances } from "actions/matters.action";
import ActionbarSkelton from "components/common/Loaders/ActionbarSkelton";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";
import { FilterComponent } from "components/common/FiltersComponent";
import GridColumnsChooser from "components/common/grid/GridColumnsChooser";
import useGridColumnSettings from "components/common/grid/useGridColumnSettings";
import MattersComboBox from "pages/Postings/MattersComboBox";
import ClientComboBox from "components/common/ClientComboBox";
import { useActivityTag } from "services/activityTagService";

const DEFAULT_PANES = [
  {
    collapsible: true,
  },
  {
    size: "0px",
  },
];

const WORK_DONE_COLUMNS = [
  { field: "type", title: "Type", width: 120, visible: true, order: 0 },
  { field: "date", title: "Date", width: 100, visible: true, order: 1 },
  {
    field: "feeEarnerRef",
    title: "FE",
    width: 60,
    visible: true,
    order: 2,
  },
  {
    field: "matterRef",
    title: "Matter Ref",
    width: 120,
    visible: true,
    order: 3,
  },
  { field: "details", title: "Details", visible: true, order: 4 },
  {
    field: "document",
    title: "Documents",
    width: 120,
    visible: true,
    order: 5,
  },
  {
    field: "email",
    title: "Email",
    width: 200,
    visible: true,
    order: 6,
  },
  {
    field: "emailFrom",
    title: "Email From",
    width: 200,
    visible: false,
    order: 7,
  },
  {
    field: "emailTo",
    title: "Email To",
    width: 200,
    visible: false,
    order: 8,
  },
  { field: "tags", title: "Tags", width: 160, visible: false, order: 9 },
  { field: "value", title: "Time Value", width: 110, visible: false, order: 10 },
  { field: "units", title: "Units", width: 80, visible: false, order: 11 },
  { field: "rate", title: "Rate", width: 90, visible: false, order: 12 },
  {
    field: "uncharged",
    title: "Uncharged",
    width: 100,
    visible: false,
    order: 13,
  },
];

const WorkDone = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getDynamicDocOpenPanes = () => [
    {
      collapsible: true,
    },
    {
      size: "40%",
      min: "500px",
      collapsible: false,
    },
  ];
  const idGetter = getter(ACTIVITY_DATA_ITEM_KEY);
  const { data: user } = useCurrentUser();
  const searchRef = useRef(null);
  const filterAnchorRef = useRef(null);
  const filterPopupRef = useRef(null);
  const {
    visibleColumns,
    renderKey: columnsRenderKey,
    gridProps: columnGridProps,
    chooserProps: columnChooserProps,
  } = useGridColumnSettings({
    gridKey: "WorkDone",
    defaultColumns: WORK_DONE_COLUMNS,
  });

  const columnsHeaderCell = useCallback(
    () => <GridColumnsChooser {...columnChooserProps} />,
    [columnChooserProps],
  );

  const tagsColumnVisible = visibleColumns.some(
    (column) => column.field === "tags",
  );

  const [activityTypes, setActivityTypes] = useState([]);
  const [selectedState, setSelectedState] = useState({});
  const [paneDocument, setPaneDocument] = useState([]);
  const [panes, setPanes] = useState(DEFAULT_PANES);
  const [loader, setLoader] = useState(true);
  const [currentMatterContext, setCurrentMatterContext] = useState({});
  const [updateFilter, setUpdateFilter] = useState(false);
  const [typeID, setTypeID] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const {
    data: allowAccessData,
    loading: teamAccessLoading,
  } = useTeamAccess();
  const [filterClicked, setFilterClicked] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [activityTypesLoading, setActivityTypesLoading] = useState(true);
  const actionBarLoading = teamAccessLoading || activityTypesLoading;

  const today = useMemo(() => new Date(), []);
  const startOfMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today],
  );

  const INITIAL_FILTER_STATE = {
    branchId: {
      branchName: "ALL",
      branchId: -2,
    },
    teamId: { teamName: "ALL", teamID: -2 },
    feeEarnerRef: { display: "ALL", userRef: "ALL" },
  };

  const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE);
  const [accessFiltersReady, setAccessFiltersReady] = useState(false);
  const [fromDate, setFromDate] = useState(startOfMonth);
  const [toDate, setToDate] = useState(today);
  const [fromDateChecked, setFromDateChecked] = useState(true);
  const [toDateChecked, setToDateChecked] = useState(false);

  const [selectedMatter, setSelectedMatter] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPopupFeeEarners, setSelectedPopupFeeEarners] = useState([]);
  const filterAutoApplySkip = useRef(false);
  const { data: activityTags } = useActivityTag();
  const [searchValue, setSearchValue] = useState("");
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [list, setLists] = useState({
    data: [],
    total: 0,
  });

  const [viewActivity, setViewActivity] = useState();
  const [isView, setView] = useState(false);
  const [multiSel, setMultiSel] = useState(false);
  const [showShareDocument, setShowShareDocument] = useState(false);
  const [currentDoc, setCurrentDoc] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [eSigning, setESigning] = useState();
  const [addActivity, setAddActivity] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [droppedFilesActivity, setDroppedFilesActivity] = useState([]);
  const [chitRequestModal, setChitRequestModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [prefilledChitDocs, setPrefilledChitDocs] = useState([]);
  const [openMenuActivityId, setOpenMenuActivityId] = useState(null);
  const [billRequestModal, setBillRequestModal] = useState(false);
  const [billingRequestPayload, setBillingRequestPayload] = useState(null);

  const createState = (skip, take) => {
    let pagerSettings = {
      pageSizes: GRID_PAGE_SIZES,
    };
    return {
      pageSize: take,
      pageable: pagerSettings,
    };
  };

  const [state] = useState(createState(0, 200));

  const [dataState, setDataState] = useState({
    take: 200,
    skip: 0,
    sort: [
      {
        field: "date",
        dir: "desc",
      },
    ],
  });

  useEffect(() => {
    if (updateFilter) {
      setUpdateFilter(false);
    }
  }, [updateFilter]);

  useEffect(() => {
    if (!filterClicked) return;

    const handlePointerDown = (event) => {
      const anchorElement = filterAnchorRef.current;
      if (anchorElement && anchorElement.contains(event.target)) {
        return;
      }

      if (event.target?.closest?.(".k-calendar")) {
        return;
      }

      if (event.target?.closest?.(".k-popup")) {
        return;
      }

      const popupElement = filterPopupRef.current;
      const popupContainer = popupElement
        ? popupElement.closest(".k-animation-container")
        : null;
      const rect =
        popupContainer?.getBoundingClientRect?.() ||
        popupElement?.getBoundingClientRect?.();

      if (!rect) {
        setFilterClicked(false);
        return;
      }

      const { clientX, clientY } = event;
      const isInside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;
      if (!isInside) {
        setFilterClicked(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [filterClicked]);

  const fetchActivityTypes = async () => {
    try {
      const types = await getActiveActivityTypes();
      setActivityTypes(types || []);
    } catch (error) { }
  };

  useEffect(() => {
    const loadActivityTypes = async () => {
      setActivityTypesLoading(true);
      try {
        await fetchActivityTypes();
      } finally {
        setActivityTypesLoading(false);
      }
    };
    loadActivityTypes();
  }, []);

  const showPaneFile = async (id, matterRef, matterId) => {
    setLoaded(false);
    try {
      if (!id) {
        setPaneDocument([]);
        setPanes(getDynamicDocOpenPanes());
        setCurrentMatterContext({});
        return;
      }
      const { data } = await getDocument(id);
      setPaneDocument([data.data]);
      setCurrentMatterContext({
        matterRef: matterRef || "",
        matterId: matterId || null,
      });
      setPanes(getDynamicDocOpenPanes());
    } catch (error) {
      setPaneDocument([]);
      setPanes(getDynamicDocOpenPanes());
      setCurrentMatterContext({});
    } finally {
      setLoaded(true);
    }
  };

  const showDocumentFile = (documentId, matterRef, matterId) => {
    if (!documentId) return;

    const encodedId = btoa(
      JSON.stringify({
        id: documentId,
        matterRef: matterRef || "",
        matterId: matterId || null,
      }),
    );

    const url = `/sa/document-viewer?id=${encodedId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (documentId, asPDF = false, fromDocView) => {
    setLoader(true);
    try {
      const selectedDocumentIds = getSelectedDocumentIds(
        selectedState,
        list?.data,
      );

      let response;
      let fileName;

      if (selectedDocumentIds.length > 1 && !fromDocView) {
        fileName = "work-done-documents.zip";
        response = await downloadDocumentsAsZip(fileName, selectedDocumentIds);
      } else if (selectedDocumentIds.length === 1 || documentId) {
        response = asPDF
          ? await downloadDocumentAsPDF(documentId || selectedDocumentIds[0])
          : await downloadDocument(documentId || selectedDocumentIds[0]);

        fileName = parseDownloadFilename(response, asPDF);
      } else {
        setLoader(false);
        return;
      }

      downloadBlobAsFile(response.data, fileName);
    } catch (error) { } finally {
      setLoader(false);
    }
  };

  const matterContextWithClosed = async (matterId, partial) => {
    if (!matterId) {
      return {
        matterRef: partial?.matterRef,
        matterId: partial?.matterId,
        closed: false,
      };
    }
    let closed = partial?.matterClosed ?? partial?.closed;
    if (closed === undefined || closed === null) {
      try {
        const { data } = await getMattersdetails(matterId);
        closed = data?.data?.closed;
      } catch (e) {
        closed = false;
      }
    }
    return {
      matterRef: partial?.matterRef,
      matterId,
      closed: Boolean(closed),
    };
  };

  const handleDuplicate = async (matterId, sourceActivity) => {
    const activityId = resolveDuplicateActivityId(sourceActivity);
    if (!matterId || !activityId) return;

    try {
      const { data } = await getMatterDocDuplicate(matterId, activityId);
      const sourceRow =
        typeof sourceActivity === "object" ? sourceActivity : null;
      const activityData = mergeDuplicateActivityData(data?.data, sourceRow);
      const mid = activityData?.matterId || matterId;

      setCurrentMatterContext(
        await matterContextWithClosed(mid, {
          matterRef: activityData?.matterRef,
          matterId: mid,
          matterClosed: activityData?.matterClosed,
          closed: activityData?.closed,
        }),
      );

      setViewActivity(activityData);
      setView(true);
      setIsDuplicate(true);
      setAddActivity(true);
    } catch (error) { }
  };

  const getDocumentEditorData = async (id, type) => {
    try {
      setLoader(true);
      const { data } = await getDocumentEditor(id, currentMatterContext.matterId);
      let editUrl = data?.data?.editURL;
      if (type === "Local") {
        let ext = "";
        if (paneDocument && paneDocument[0]) {
          const doc = paneDocument[0];
          if (doc.documentName) {
            ext = doc.documentName.split(".").pop().toLowerCase();
          }
        }
        if (["xls", "xlsx"].includes(ext)) {
          editUrl = `ms-excel:ofe|u|${editUrl}`;
        } else {
          editUrl = `ms-word:ofe|u|${editUrl}`;
        }
      }
      window.open(editUrl, "_blank");
      showPaneFile(
        id,
        currentMatterContext.matterRef,
        currentMatterContext.matterId,
      );
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode !== 460) {
      }
    } finally {
      setLoader(false);
    }
  };

  const handleActivityRowClick = async (activity) => {
    const matterId = activity?.matterId;
    const activityId = activity?.activityId;

    const fetchLatestActivity = async () => {
      if (!matterId || !activityId) return activity;
      try {
        const { data } = await getSingleMatterActivity(matterId, activityId);
        return data?.data ?? data ?? activity;
      } catch (error) {
        toast.error("Could not load the latest activity details.");
        return activity;
      }
    };

    if (activity.subType === "ChitRequest") {
      const latestActivity = await fetchLatestActivity();
      const chitId =
        latestActivity?.chitRequestId ??
        latestActivity?.chitId ??
        latestActivity?.subTypeId;
      if (!chitId) {
        return;
      }
      setSelectedActivity(latestActivity);
      setCurrentMatterContext(
        await matterContextWithClosed(latestActivity.matterId, {
          matterRef: latestActivity.matterRef,
          matterId: latestActivity.matterId,
          matterClosed: latestActivity.matterClosed,
          closed: latestActivity.closed,
        }),
      );
      setChitRequestModal(true);
      return;
    }

    if (activity.subType === "BillRequest") {
      const latestActivity = await fetchLatestActivity();
      const billRequestId = latestActivity?.subTypeId;
      if (!billRequestId) {
        return;
      }
      try {
        const response = await getBillingRequestDetails(billRequestId);
        const detailData = response?.data?.data;
        if (!detailData) {
          return;
        }
        const templateFromDetail = detailData.billTemplateResourceId;
        const payload = {
          ...latestActivity,
          ...detailData,
          matterRef:
            detailData.matterRef ||
            latestActivity.matterRef ||
            currentMatterContext.matterRef ||
            "",
        };
        if (isNonEmptyBillResourceId(templateFromDetail)) {
          payload.billTemplateResourceId = templateFromDetail;
        }
        setCurrentMatterContext(
          await matterContextWithClosed(payload.matterID ?? payload.matterId, {
            matterRef: payload.matterRef,
            matterId: payload.matterID ?? payload.matterId,
            matterClosed: latestActivity.matterClosed,
            closed: latestActivity.closed,
          }),
        );
        setBillingRequestPayload(payload);
        setBillRequestModal(true);
      } catch (error) { }
      return;
    }

    setSelectedState({
      [activity.activityId]: true,
    });
    setCurrentMatterContext(
      await matterContextWithClosed(activity.matterId, {
        matterRef: activity.matterRef,
        matterId: activity.matterId,
        matterClosed: activity.matterClosed,
        closed: activity.closed,
      }),
    );
    const latestActivity = await fetchLatestActivity();
    setViewActivity(latestActivity);
    setView(true);
    setAddActivity(true);
  };

  const setCurrentMatterForShare = (matterContext) => {
    setCurrentMatterContext(matterContext);
  };

  const getActivitiesData = () => {
    handleRefresh();
  };

  const handleDeleteClick = (dataItem) => {
    setDeleteConfirmation(
      getSelectedActivities(dataItem, selectedState, list?.data),
    );
  };

  const deleteMatterActivity = async (items) => {
    const itemsArray = Array.isArray(items) ? items : [items];
    try {
      setLoader(true);
      await Promise.all(
        itemsArray.map((data) =>
          updateMatterActivity(data.matterId, data.activityId, {
            ...data,
            active: false,
          }),
        ),
      );
      setDeleteConfirmation(false);
      setSelectedState({});
      toast.success(getDeleteSuccessMessage(itemsArray));
      handleRefresh();
    } catch (error) { } finally {
      setLoader(false);
    }
  };

  const onChange = (event) => {
    if (event?.newState[0].collapsed) {
      setPanes([
        {
          ...event?.newState[0],
        },
        {},
      ]);
      return;
    } else if (
      !event?.newState[1].collapsed &&
      !event?.newState[1].size &&
      paneDocument.length > 0
    ) {
      setPanes(getDynamicDocOpenPanes());
      return;
    } else {
      setPanes([...event.newState]);
    }
  };

  const handleAttachToChit = (docs) => {
    if (!docs?.length) return;
    setPrefilledChitDocs(docs);
    setSelectedActivity(null);
    setChitRequestModal(true);
  };

  const findRowWithDocForSelection = (oldState, newState) => {
    for (const id of Object.keys(newState)) {
      if (newState[id] && !oldState?.[id]) {
        const row = list?.data?.find((item) => item.activityId == id);
        if (row?.documentId) return row;
      }
    }
    const selectedIds = Object.keys(newState || {}).filter((k) => newState[k]);
    for (let i = selectedIds.length - 1; i >= 0; i--) {
      const row = list?.data?.find((item) => item.activityId == selectedIds[i]);
      if (row?.documentId) return row;
    }
    return null;
  };

  const onSelectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: ACTIVITY_DATA_ITEM_KEY,
      });
      setMultiSel(true);
      setSelectedState(newSelectedState);

      const row = findRowWithDocForSelection(selectedState, newSelectedState);
      if (row) {
        showPaneFile(row.documentId, row.matterRef, row.matterId);
      } else {
        setPaneDocument([]);
        setPanes(DEFAULT_PANES);
        setCurrentMatterContext({});
      }
    },
    [selectedState, list?.data],
  );

  const onHeaderSelectionChange = useCallback(
    (event) => {
      const checkboxElement = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState = {};
      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    [idGetter],
  );

  const dataStateChange = (e) => {
    const nextSort = e.dataState.sort;
    const prevSortField = dataState.sort?.[0]?.field ?? "date";

    if (
      nextSort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== nextSort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (nextSort && nextSort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: prevSortField,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
    setUpdateFilter(true);
  };

  const TypeCell = (prop) => {
    const handleTypeClick = (e) => {
      e.stopPropagation();
      handleActivityRowClick(prop.dataItem);
    };

    return (
      <td className="fs-sm wrap-text">
        <u style={{ cursor: "pointer" }} onClick={handleTypeClick}>
          {prop.dataItem.type}
        </u>
      </td>
    );
  };

  const DateCell = (props) => {
    const dateField = props.dataItem[props.field];

    if (dateField) {
      const dt = new Date(dateField);
      const dateStr = dt.toLocaleDateString("en-GB");
      const timeStr = dt.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return (
        <td
          onClick={(e) => {
            e.stopPropagation();
            setSelectedState({
              [props.dataItem.activityId]: true,
            });
            showPaneFile(
              props.dataItem?.documentId,
              props.dataItem?.matterRef,
              props.dataItem?.matterId,
            );
          }}
          className={`fs-sm ${props.className}`}>
          <div className="text-nowrap">{dateStr}</div>
          <div className="text-nowrap">{timeStr}</div>
        </td>
      );
    } else {
      return <td></td>;
    }
  };

  const DocumentCell = (e) => {
    const { documentId, documentName, matterRef, matterId } = e.dataItem;

    const hybridClick = useDoubleClick(
      () => showDocumentFile(documentId, matterRef, matterId),
      () => {
        setSelectedState({
          [e.dataItem.activityId]: true,
        });
        showPaneFile(documentId, matterRef, matterId);
      },
    );

    return documentId ? (
      <td className={e.className ?? ""} style={e.style} onClick={hybridClick}>
        <div role="button" className="d-flex">
          {fileIcon(documentName)}
          <span className="ps-2 fs-sm d-lg-none d-xl-block">
            {truncateFileName(documentName)}
          </span>
          <span className="ps-2 fs-sm d-xl-none d-lg-block">
            {documentName?.split(".").pop()}
          </span>
        </div>
      </td>
    ) : (
      <td />
    );
  };

  const EmailCell = (props) => {
    const { emailFrom, emailTo, documentId, activityId, matterRef, matterId } =
      props.dataItem;

    return (
      <td
        className={`fs-sm wrap-text ${props.className}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedState({
            [activityId]: true,
          });
          showPaneFile(documentId, matterRef, matterId);
        }}>
        <div className="text-nowrap">{emailFrom}</div>
        <div className="text-nowrap">{emailTo}</div>
      </td>
    );
  };

  const DetailsCell = (props) => {
    const activity = props.dataItem;
    const status = getActivitySubtypeStatus(activity);

    const { documentId, matterRef, matterId, activityId } = activity;

    return (
      <td
        onClick={(e) => {
          e.stopPropagation();
          setSelectedState({
            [activityId]: true,
          });
          if (documentId) {
            showPaneFile(documentId, matterRef, matterId);
          }
        }}
        className={`wrap-text ${props.className} cursor-default`}>
        <div className="d-flex align-items-center justify-content-between gap-2 w-100">
          <div className="flex-grow-1 text-wrap" style={{ minWidth: 0 }}>
            {status && (
              <span
                className={`me-2 badge ${getStatusClass(status)}`}
                style={{
                  padding: "0.50em 0.75em 0.35em",
                }}
              >
                {status}
              </span>
            )}
            {activity?.details}
            {!tagsColumnVisible && activity?.tags?.length > 0 && (
              <span className="d-inline-flex flex-wrap gap-1 ms-2 align-middle">
                {activity.tags.map((tag) => (
                  <span
                    key={getActivityTagId(tag)}
                    title={tag.name}
                    style={getActivityTagChipStyle(tag)}
                  >
                    {tag.name}
                  </span>
                ))}
              </span>
            )}
          </div>
          <MenuCell
            noTd
            dataItem={activity}
            loaded={loaded}
            setLoaded={setLoaded}
            setSelectedState={setSelectedState}
            selectedState={selectedState}
            showPaneFile={showPaneFile}
            multiSel={multiSel}
            setMultiSel={setMultiSel}
            setAddActivity={setAddActivity}
            setViewActivity={setViewActivity}
            setView={setView}
            handleActivityRowClick={handleActivityRowClick}
            showDocumentFile={showDocumentFile}
            handleDownload={handleDownload}
            getDocumentEditorData={getDocumentEditorData}
            handleDuplicate={handleDuplicate}
            setShowShareDocument={setShowShareDocument}
            setCurrentDoc={setCurrentDoc}
            onDeleteClick={handleDeleteClick}
            setESigning={setESigning}
            setCurrentMatterForShare={setCurrentMatterForShare}
            matterId={activity?.matterId}
            activities={list?.data}
            onAttachToChit={handleAttachToChit}
            isDocViewerOpen={
              panes[1]?.size &&
              panes[1]?.size !== "0px" &&
              panes[1]?.size !== "0" &&
              panes[1]?.size !== 0
            }
            openMenuActivityId={openMenuActivityId}
            setOpenMenuActivityId={setOpenMenuActivityId}
          />
        </div>
      </td>
    );
  };

  const MatterRefCell = (prop) => {
    const matterId = prop.dataItem?.matterId;
    const matterRef = prop.dataItem?.matterRef || "";

    if (!matterId || !matterRef) {
      return <td className="fs-sm">{matterRef}</td>;
    }

    const openCaseFile = (e) => {
      e?.stopPropagation?.();
      try {
        window.chrome.webview.postMessage("CasefileOpened:" + matterId);
      } catch (error) { }
      navigate(`/matters/${matterId}`);
    };

    return (
      <td className="fs-sm">
        <span
          style={{ cursor: "pointer" }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openCaseFile(e);
            }
          }}
          onClick={openCaseFile}
        >
          <u>{matterRef}</u>
        </span>
      </td>
    );
  };

  const dataReceived = (data) => {
    if (data) {
      setLists(data);
      setLoader(false);
    }
  };

  const handleRefresh = () => {
    setDataState({
      ...dataState,
      skip: 0,
    });
    setSelectedState({});
    setPaneDocument([]);
    setPanes(DEFAULT_PANES);
    setCurrentMatterContext({});
    setIsRefreshed(!isRefreshed);
    setUpdateFilter(true);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value || "");
    setUpdateFilter(true);
  };

  useEffect(() => {
    if (filterAutoApplySkip.current) {
      filterAutoApplySkip.current = false;
      return;
    }

    setDataState((current) =>
      current.skip === 0 ? current : { ...current, skip: 0 },
    );

    const isFromDateDifferent =
      !fromDateChecked ||
      !fromDate ||
      fromDate.getFullYear() !== startOfMonth.getFullYear() ||
      fromDate.getMonth() !== startOfMonth.getMonth() ||
      fromDate.getDate() !== startOfMonth.getDate();
    const isToDateDifferent = toDateChecked || toDate === null;
    const filterStatus =
      isFromDateDifferent ||
      isToDateDifferent ||
      typeID.length > 0 ||
      selectedMatter !== null ||
      selectedClient !== null ||
      selectedTags.length > 0 ||
      selectedPopupFeeEarners.length > 0;
    setFilterApplied(filterStatus);
  }, [
    fromDate,
    toDate,
    fromDateChecked,
    toDateChecked,
    typeID,
    selectedMatter,
    selectedClient,
    selectedTags,
    selectedPopupFeeEarners,
    startOfMonth,
  ]);

  const handleFilterReset = () => {
    filterAutoApplySkip.current = true;
    setFromDate(startOfMonth);
    setToDate(today);
    setFromDateChecked(true);
    setToDateChecked(false);
    setTypeID([]);
    setSelectedMatter(null);
    setSelectedClient(null);
    setSelectedTags([]);
    setSelectedPopupFeeEarners([]);
    setDataState((current) => ({ ...current, skip: 0 }));
    setFilterApplied(false);
    setFilterClicked(false);
  };

  const popupFeeEarnerOptions = useMemo(() => {
    const byRef = new Map();
    (allowAccessData.branches || []).forEach((branch) => {
      (branch.teams || []).forEach((team) => {
        (team.feeEarners || []).forEach((feeEarner) => {
          if (feeEarner?.userRef && feeEarner.userRef !== "ALL") {
            byRef.set(feeEarner.userRef, {
              ...feeEarner,
              display:
                feeEarner.display ||
                (feeEarner.displayname
                  ? `${feeEarner.userRef} - ${feeEarner.displayname}`
                  : feeEarner.userRef),
            });
          }
        });
      });
    });
    return [...byRef.values()].sort((left, right) =>
      (left.display || "").localeCompare(right.display || ""),
    );
  }, [allowAccessData.branches]);

  const selectedTypeIds = useMemo(
    () =>
      typeID
        .map((type) => type.activityTypeID)
        .filter((id) => id !== "" && id != null),
    [typeID],
  );
  const selectedTagIds = useMemo(
    () =>
      selectedTags
        .map((tag) => getActivityTagId(tag))
        .filter((id) => id != null),
    [selectedTags],
  );
  const selectedPopupFeeEarnerRefs = useMemo(
    () => selectedPopupFeeEarners.map((feeEarner) => feeEarner.userRef),
    [selectedPopupFeeEarners],
  );

  const mappedData = buildMappedGridData(list, selectedState, idGetter);

  return (
    <div className="activity-grid d-flex flex-column h-100 px-4">
      {actionBarLoading ? (
        <ActionbarSkelton length={3} />
      ) : (
        <div
          className={`d-flex align-items-end gap-2 py-3 justify-content-between ${styles.actionBarRow}`}>
          <div className={`d-flex gap-2 align-items-end ${styles.actionBarFilters}`}>
            <FilterComponent
              allowAccessData={allowAccessData}
              inlineLayout={true}
              disabledFeeEarner={false}
              updateFilters={(filters) => {
                setFilterState({
                  branchId: {
                    branchName: "ALL",
                    branchId: filters.branch,
                  },
                  teamId: {
                    teamName: "ALL",
                    teamID: filters.teamId,
                  },
                  feeEarnerRef: {
                    display: filters.feeEarnerRef,
                    userRef: filters.feeEarnerRef,
                  },
                });
                setAccessFiltersReady(true);
                setUpdateFilter(true);
              }}
            />
          </div>
          <div className="d-flex gap-1 align-items-end">
            <div
              style={{
                flex: "1 1 0%",
                minWidth: "180px",
                maxWidth: "260px",
              }}>
              <SearchTextBox
                width="100%"
                height="40px"
                ref={searchRef}
                changeFilterValue={handleSearchChange}
              />
            </div>
            <div
              ref={filterAnchorRef}
              onClick={() => setFilterClicked(!filterClicked)}
              className={styles.filterBtnWrap}>
              {!filterApplied ? (
                <Button className={`common-btn ${styles.customBtn}`}>
                  <FaFilter color="white" size={16} />
                </Button>
              ) : (
                <Button className={`common-btn ${styles.customBtn}`}>
                  <img
                    src={filtersAppliedIcon}
                    alt="Filters Applied"
                    width={25}
                    height={25}
                  />
                </Button>
              )}
            </div>
            <Button
              className={`common-btn  ${styles.customBtn}`}
              svgIcon={refreshIcon}
              onClick={handleRefresh}
              title="Refresh"
            />
          </div>
        </div>
      )}
      <div className="h-100 flex-grow-1" style={{ minHeight: 0 }}>
        <Splitter
          panes={panes}
          onChange={onChange}
          onBarToggle={() => {
            if (panes[0].collapsed) {
              setPanes(getDynamicDocOpenPanes());
            } else {
              setPanes([
                {
                  collapsed: true,
                },
                {},
              ]);
            }
          }}
          className={`workflow-splitter border-0 h-100 ${(() => {
              const sz = panes[1]?.size;
              const open = sz && sz !== "0px" && sz !== "0" && sz !== 0;
              return !open ? styles["hide-splitbar"] : "";
            })()
            }`}>
          <Grid
            key={columnsRenderKey}
            {...dataState}
            {...columnGridProps}
            data={mappedData}
            sortable={true}
            dataItemKey={ACTIVITY_DATA_ITEM_KEY}
            selectedField={ACTIVITY_SELECTED_FIELD}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            onDataStateChange={dataStateChange}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            className="h-100 overflow-auto grid-row-select casefile_outlook_grid"
            pageable={state.pageable}
            pageSize={state.pageSize}
            rowRender={(row, props) => {
              const style = getRowStyle(props.dataItem, activityTypes);
              return React.cloneElement(row, {
                style: { ...row.props.style, ...style },
              });
            }}>
            <GridNoRecords>
              {loader ? (
                <div style={{ height: "100px" }}></div>
              ) : (
                mappedData.data &&
                mappedData.data.length === 0 &&
                "No record available"
              )}
            </GridNoRecords>
            <GridColumn
              field={ACTIVITY_SELECTED_FIELD}
              width={50}
              headerCell={columnsHeaderCell}
              reorderable={false}
              resizable={false}
              headerSelectionValue={
                mappedData?.data?.findIndex(
                  (item) => !selectedState[idGetter(item)],
                ) === -1
              }
            />
            {visibleColumns.map((column) => {
              const width = column.width ?? undefined;
              switch (column.field) {
                case "type":
                  return (
                    <GridColumn
                      key="type"
                      field="type"
                      title="Type"
                      cell={TypeCell}
                      width={width}
                      minResizableWidth={60}
                      className="fs-sm"
                    />
                  );
                case "date":
                  return (
                    <GridColumn
                      key="date"
                      field="date"
                      title="Date"
                      cell={DateCell}
                      width={width}
                      minResizableWidth={80}
                      className="p-1 cursor-default"
                    />
                  );
                case "feeEarnerRef":
                  return (
                    <GridColumn
                      key="feeEarnerRef"
                      field="feeEarnerRef"
                      title="FE"
                      width={width}
                      minResizableWidth={40}
                      className="cursor-default"
                    />
                  );
                case "matterRef":
                  return (
                    <GridColumn
                      key="matterRef"
                      field="matterRef"
                      title="Matter Ref"
                      cell={MatterRefCell}
                      width={width}
                      minResizableWidth={80}
                    />
                  );
                case "details":
                  return (
                    <GridColumn
                      key="details"
                      field="details"
                      title="Details"
                      cell={DetailsCell}
                      width={width}
                      minResizableWidth={180}
                      className="text-wrap cursor-default"
                    />
                  );
                case "document":
                  return (
                    <GridColumn
                      key="document"
                      field="document"
                      title="Documents"
                      cell={DocumentCell}
                      width={width}
                      minResizableWidth={80}
                      sortable={false}
                      className="fs-sm px-1"
                    />
                  );
                case "email":
                  return (
                    <GridColumn
                      key="email"
                      field="email"
                      title="Email"
                      cell={EmailCell}
                      width={width}
                      minResizableWidth={100}
                      sortable={false}
                      className="fs-sm px-1 cursor-default"
                    />
                  );
                case "emailFrom":
                  return (
                    <GridColumn
                      key="emailFrom"
                      field="emailFrom"
                      title="Email From"
                      width={width}
                      minResizableWidth={100}
                      className="fs-sm px-1 cursor-default"
                    />
                  );
                case "emailTo":
                  return (
                    <GridColumn
                      key="emailTo"
                      field="emailTo"
                      title="Email To"
                      width={width}
                      minResizableWidth={100}
                      className="fs-sm px-1 cursor-default"
                    />
                  );
                case "tags":
                  return (
                    <GridColumn
                      key="tags"
                      field="tags"
                      title="Tags"
                      cell={ActivityTagsCell}
                      width={width}
                      minResizableWidth={80}
                      sortable={false}
                      className="cursor-default"
                    />
                  );
                case "value":
                  return (
                    <GridColumn
                      key="value"
                      field="value"
                      title="Time Value"
                      cell={ActivityAmountCell}
                      width={width}
                      minResizableWidth={70}
                      className="cursor-default"
                    />
                  );
                case "units":
                  return (
                    <GridColumn
                      key="units"
                      field="units"
                      title="Units"
                      cell={ActivityUnitsCell}
                      width={width}
                      minResizableWidth={60}
                      className="cursor-default"
                    />
                  );
                case "rate":
                  return (
                    <GridColumn
                      key="rate"
                      field="rate"
                      title="Rate"
                      cell={ActivityAmountCell}
                      width={width}
                      minResizableWidth={60}
                      className="cursor-default"
                    />
                  );
                case "uncharged":
                  return (
                    <GridColumn
                      key="uncharged"
                      field="uncharged"
                      title="Uncharged"
                      cell={ActivityUnchargedCell}
                      width={width}
                      minResizableWidth={70}
                      className="cursor-default"
                    />
                  );
                default:
                  return null;
              }
            })}
            <GridColumn
              reorderable={false}
              resizable={false}
              width={1}
              headerCell={() => null}
              headerClassName="activities-spacer-col"
              className="activities-spacer-col"
            />
          </Grid>

          <div className="h-100">
            <DocumentViewer
              loader={loaded}
              onDownload={handleDownload}
              data={paneDocument}
              onRefresh={(id) =>
                showPaneFile(
                  id,
                  currentMatterContext.matterRef,
                  currentMatterContext.matterId,
                )
              }
              onScreenComponent={true}
              onClose={() => {
                setPanes(DEFAULT_PANES);
                setPaneDocument([]);
                setSelectedState({});
                setCurrentMatterContext({});
              }}
              onExpand={() => {
                if (panes[0].collapsed) {
                  setPanes(getDynamicDocOpenPanes());
                } else {
                  setPanes([
                    {
                      collapsed: true,
                    },
                    {},
                  ]);
                }
              }}
              onOpenInNewTab={(documentId) =>
                showDocumentFile(
                  documentId,
                  currentMatterContext.matterRef,
                  currentMatterContext.matterId,
                )
              }
              matterId={currentMatterContext.matterId}
              matter={currentMatterContext}
              component="workDone"
            />
          </div>
        </Splitter>
      </div>

      <Popup
        key={filterClicked ? "open" : "closed"}
        show={filterClicked}
        anchor={filterAnchorRef?.current}
        anchorAlign={{
          horizontal: "right",
          vertical: "bottom",
        }}
        popupAlign={{
          horizontal: "right",
          vertical: "top",
        }}
        popupClass={`${styles["popup-content"]}`}>
        <div
          ref={(el) => {
            filterPopupRef.current = el;
          }}
          role="menu"
          className="d-flex flex-column align-items-center">
          <div className="d-flex align-items-start flex-column p-2 mx-2" style={{ width: 250 }}>
            <Label className="mt-2">Set Filters</Label>
            <hr className="w-100 mt-2 " />
            <div style={{ width: "220px" }}>
              <div>
                <Label editorId="from" className="me-5 pe-5 mb-1">
                  From
                </Label>
              </div>
              <div className="date-form-to-wrapper">
                <Checkbox
                  className="ms-2 mt-auto mb-auto"
                  value={fromDateChecked}
                  onChange={(e) => setFromDateChecked(e.value)}
                />
                <div className="from">
                  <DatePicker
                    id="from"
                    className="date-picker margin-date"
                    format="dd/MM/yyyy"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.value);
                      setFromDateChecked(true);
                    }}
                    toggleButton={CalenderCustomToggleButton}
                  />
                </div>
              </div>
              <div className="mt-2">
                <Label editorId="to" className=" mb-1">
                  To
                </Label>
                <div className="date-form-to-wrapper">
                  <Checkbox
                    className="ms-2 mt-auto mb-auto"
                    value={toDateChecked}
                    onChange={(e) => setToDateChecked(e.value)}
                  />
                  <div className="to">
                    <DatePicker
                      id="to"
                      className="date-picker margin-date "
                      format="dd/MM/yyyy"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.value);
                        setToDateChecked(true);
                      }}
                      toggleButton={CalenderCustomToggleButton}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="my-2 d-flex flex-column ">
              <Label className="label mb-1">Type</Label>
              <MultiSelect
                className={styles.filterMultiSelect}
                style={{ width: "220px" }}
                textField="name"
                dataItemKey="activityTypeID"
                data={activityTypes}
                value={typeID}
                placeholder="Select Types"
                onChange={(e) => setTypeID(e.value || [])}
                popupSettings={{ appendTo: document.body }}
              />
            </div>
            <div className="my-2 d-flex flex-column">
              <Label className="label mb-1">Matter</Label>
              <MattersComboBox
                controlled
                value={selectedMatter}
                width={220}
                onChangematter={(event) =>
                  setSelectedMatter(event?.value ?? event?.target?.value ?? null)
                }
              />
            </div>
           
            <div className="my-2 d-flex flex-column">
              <Label className="label mb-1">Client Ref</Label>
              <ClientComboBox
                width="220px"
                height="40px"
                required={false}
                val={selectedClient}
                onChangeClient={setSelectedClient}
              />
            </div>
            <div className="my-2 d-flex flex-column">
              <Label className="label mb-1">Tags</Label>
              <MultiSelect
                className={styles.filterMultiSelect}
                style={{ width: "220px" }}
                textField="name"
                dataItemKey="tagId"
                data={(activityTags || []).filter((tag) => tag.active !== false)}
                value={selectedTags}
                placeholder="Select Tags"
                onChange={(event) => setSelectedTags(event.value || [])}
                popupSettings={{ appendTo: document.body }}
              />
            </div>
            <div className="d-flex my-2 w-100">
              <Button
                className="common-btn w-100"
                onClick={handleFilterReset}>
                Reset
              </Button>
            </div>
          </div>
        </div>
      </Popup>

      {accessFiltersReady && <HandleGridBody
        dataState={dataState}
        currentComponent="workDone"
        branchId={filterState.branchId?.branchId}
        teamId={filterState.teamId?.teamID}
        feeEarnerRef={feeEarnerRefIfAllowed(
          filterState.feeEarnerRef?.userRef,
          allowAccessData,
        )}
        fD={fromDateChecked && fromDate ? formatDateForAPI(fromDate) : null}
        tD={toDateChecked && toDate ? formatDateForAPI(toDate) : null}
        typeId={selectedTypeIds}
        tagIds={selectedTagIds}
        matterId={selectedMatter?.matterId ?? null}
        clientId={selectedClient?.clientId ?? selectedClient?.CustomerID ?? null}
        activityFeeEarnerRefs={selectedPopupFeeEarnerRefs}
        onDataReceived={dataReceived}
        searchValue={searchValue}
        updateFilter={updateFilter}
        refresh={isRefreshed}
      />}

      {showShareDocument && (
        <ShareDocumentModal
          onClose={() => setShowShareDocument(false)}
          selectedDocuments={getSelectedDocumentsForShare(
            selectedState,
            list?.data,
          )}
          matterId={currentMatterContext?.matterId}
          matter={currentMatterContext}
          currentDoc={currentDoc}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationDialog
          content={getDeleteConfirmationMessage(deleteConfirmation)}
          onClickYes={() => {
            deleteMatterActivity(deleteConfirmation);
          }}
          onClickNo={() => setDeleteConfirmation(false)}
          onClose={() => setDeleteConfirmation(false)}
        />
      )}

      {eSigning && (
        <ESigning
          matterId={currentMatterContext?.matterId}
          title={"Select Contacts"}
          activityId={eSigning}
          onClose={() => setESigning()}
        />
      )}

      {addActivity && (
        <CaseActivityForm
          getActivitiesData={getActivitiesData}
          addFormText={`${currentMatterContext?.matterRef || ""} - Work Done`}
          droppedFilesActivity={droppedFilesActivity}
          viewActivity={viewActivity}
          feeEarnerRef={viewActivity?.fE || user?.userRef}
          matterDetails={{
            ...currentMatterContext,
            feeEarnerRef: viewActivity?.fE || user?.userRef,
          }}
          isView={isView}
          isDuplicate={isDuplicate}
          reference={currentMatterContext?.matterId}
          onMatterRefClickActions={() => { }}
          setshowMatterForm={() => {
            setViewActivity();
            setView(false);
            setDroppedFilesActivity([]);
            if (isDuplicate) setIsDuplicate(false);
            setAddActivity(false);
            setCurrentMatterContext({});
          }}
          isAddActivityBtn={false}
        />
      )}

      {chitRequestModal && (selectedActivity || prefilledChitDocs.length > 0) && (
        <ChitRequest
          cancelEdit={() => {
            setChitRequestModal(false);
            setSelectedActivity(null);
            setPrefilledChitDocs([]);
            dispatch(clearMattersBalances("Chit Request"));
          }}
          matter={
            selectedActivity
              ? {
                matterId:
                  selectedActivity.matterId ??
                  selectedActivity.matterID ??
                  currentMatterContext.matterId,
                matterRef:
                  selectedActivity.matterRef ?? currentMatterContext.matterRef,
                feeEarnerRef: selectedActivity.feeEarnerRef,
              }
              : undefined
          }
          matterId={
            selectedActivity
              ? (selectedActivity.matterId ??
                selectedActivity.matterID ??
                currentMatterContext.matterId)
              : undefined
          }
          component="modal"
          chitRequestId={
            selectedActivity
              ? (selectedActivity?.chitRequestId ??
                selectedActivity?.chitId ??
                selectedActivity?.subTypeId)
              : undefined
          }
          unAllocated={Boolean(selectedActivity?.unallocatedOnly)}
          viewMode="dialog"
          activityData={selectedActivity || undefined}
          prefilledDocuments={prefilledChitDocs}
        />
      )}

      {billRequestModal && billingRequestPayload && (
        <BillPostingModal
          billingRequestPayload={billingRequestPayload}
          onClose={() => {
            setBillRequestModal(false);
            setBillingRequestPayload(null);
          }}
          onPostSuccess={() => {
            setBillRequestModal(false);
            setBillingRequestPayload(null);
            getActivitiesData();
          }}
        />
      )}
    </div>
  );
};

export default WorkDone;
